import { useEffect, useState } from 'react';

interface Props {
  /** Appelé à chaque changement : `true` quand la vérification humaine est passée. */
  onValidityChange: (valid: boolean) => void;
}

/**
 * AntiBotGuard — protection légère des formulaires d'inscription.
 *
 * Combine deux défenses :
 *  1. un « honeypot » invisible (champ que seuls les robots remplissent) ;
 *  2. une question humaine simple (somme) que les scripts basiques ratent.
 *
 * Le composant signale au parent si la vérification est valide. Le parent doit
 * bloquer la soumission tant que ce n'est pas le cas.
 */
export default function AntiBotGuard({ onValidityChange }: Props) {
  const [trap, setTrap] = useState('');
  const [answer, setAnswer] = useState('');
  const [challenge] = useState(() => {
    const a = 2 + Math.floor(Math.random() * 6);
    const b = 1 + Math.floor(Math.random() * 6);
    return { a, b, sum: a + b };
  });

  const humanOk = answer.trim() !== '' && Number(answer) === challenge.sum;
  const valid = trap.trim() === '' && humanOk;

  useEffect(() => {
    onValidityChange(valid);
  }, [valid, onValidityChange]);

  return (
    <div>
      <label htmlFor="ab-answer" className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 mb-1.5">
        <i className="ri-shield-check-line text-accent-600"></i>
        Vérification anti-robot
        <span className="text-red-400">*</span>
      </label>
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 border border-background-200/70 text-sm font-semibold text-foreground-800 whitespace-nowrap">
          {challenge.a} + {challenge.b} =
        </span>
        <input
          id="ab-answer"
          name="antibot_answer"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={answer}
          onChange={(e) => setAnswer(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="Votre réponse"
          className="flex-1 h-11 px-4 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 focus:ring-1 focus:ring-accent-300 transition-colors"
        />
        {humanOk && (
          <i className="ri-checkbox-circle-line text-accent-500 text-xl flex-shrink-0" aria-hidden="true"></i>
        )}
      </div>
      <p className="text-xs text-foreground-400 mt-1.5">
        Cette étape nous aide à bloquer les inscriptions automatisées.
      </p>

      {/* Honeypot invisible — laissé vide par les humains, rempli par les robots. */}
      <div className="form-hp-field" aria-hidden="true">
        <label htmlFor="website_alt">Site web</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          value={trap}
          onChange={(e) => setTrap(e.target.value)}
        />
      </div>
    </div>
  );
}